import React, {useCallback, useEffect, useState} from "react";
import PropTypes from "prop-types";
import {IntlProvider} from "react-intl";
import {LANGUAGE, loadMessagesJson} from "../../common/i18n/locales/localeConstant";
import {checkIfLocaleIsInUrl, getLanguage} from "../../common/common";
import {ThemeProvider as MuiThemeProvider} from "@material-ui/styles";
import {createMuiTheme} from "@material-ui/core";
import defaultTheme, {getMUILanguage} from "../../common/theme";
import * as muiLocales from "@material-ui/core/locale";
import {useCrowdinTranslations, useSessionStorage} from "../../hooks";

const IntlWrapperProvider = ({children, locale}) => {
    const [messages, setMessages] = useState();
    const [redirecting, setRedirecting] = useState(false);
    const {getTranslations, isInit: crowdinInit} = useCrowdinTranslations();
    const [fallbackMessages, setFallbackMessages] = useSessionStorage("USE_TRANSLATION_FALLBACK_MESSAGES", false);
    const missingTranslationsMap = new Set();

    const getLocalTranslations = useCallback(async (locale) => {
        if (!locale) {
            return;
        }

        const messages = await loadMessagesJson(locale);
        return messages;

    }, [locale]);

    useEffect(() => {
        if (locale && crowdinInit) {
            const loadMessages = async () => {
                try {
                    const parts = locale?.split("-");
                    const crowdinLocale = parts && parts?.[1] === parts?.[0]
                        ? parts[0]
                        : (parts[0] + "-" + (parts[1] ? parts[1].toUpperCase() : ""));
                    const msg = await getTranslations(crowdinLocale);
                    setMessages(msg);
                } catch (err) {
                    loadMessagesFromFile();
                    setFallbackMessages("USE_TRANSLATION_FALLBACK_MESSAGES", true);
                }
            };

            const loadMessagesFromFile = async () => {
                const msg = await getLocalTranslations(locale);
                setMessages(msg);
            };

            if (fallbackMessages) {
                loadMessagesFromFile();
            } else {
                loadMessages();
            }
        }
    }, [locale, crowdinInit, getLocalTranslations]);

    const handleMissingTranslations = useCallback(() => {
        if (!redirecting) {
            setRedirecting(true);
            missingTranslationsMap.forEach((missingTranslation) => {
                console.error("Missing translation: " + missingTranslation);
            });
            if (/^de-at$|^de-ch$/.test(getLanguage().toLowerCase())) {
                window.localStorage.setItem("locale", LANGUAGE.DE);
                window.location.assign(
                    window.location.origin + checkIfLocaleIsInUrl(window.location.pathname)
                        ? window.location.pathname.replace(/\/[a-z]{2}-[a-z]{2}\//, `/${LANGUAGE.DE}`) +
                              window.location.search
                        : `/${LANGUAGE.DE}`
                );
            } else {
                window.localStorage.setItem("locale", LANGUAGE.EN);
                window.location.assign(
                    window.location.origin + checkIfLocaleIsInUrl(window.location.pathname)
                        ? window.location.pathname.replace(/\/[a-z]{2}-[a-z]{2}\//, `/${LANGUAGE.EN}/`) + window.location.search
                        : `/${LANGUAGE.EN}`
                );
                setFallbackMessages("USE_TRANSLATION_FALLBACK_MESSAGES", true);
            }
        }
    }, [redirecting, getLocalTranslations]);

    const myCustomErrorFunction = (error) => {
        let redirectTimeout;

        // Disable fallback on missing data
        // TODO - Remove check when new translations have been added
        if (error?.code !== "MISSING_DATA" && error?.descriptor?.id) {
            missingTranslationsMap.add(error.descriptor.id);
            clearTimeout(redirectTimeout);
            redirectTimeout = setTimeout(handleMissingTranslations, 1000);
        }
    };

    if (messages) {
        return (
            <IntlProvider
                locale={locale}
                messages={messages}
                onError={myCustomErrorFunction}
            >
                <MuiThemeProvider theme={createMuiTheme(defaultTheme, muiLocales[getMUILanguage(locale)])}>
                    {children}
                </MuiThemeProvider>
            </IntlProvider>
        );
    } else {
        return null;
    }

};

export default IntlWrapperProvider;

IntlWrapperProvider.propTypes = {
    children: PropTypes.any,
    locale: PropTypes.string
};
